/**
 * Courses Page - Individual Adult Learner
 * Displays available courses for self-paced learning
 * Epic 75 - Individual Adult Self-Paced Learning
 */

import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Search,
  Tile,
  Button,
  Tag,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  OverflowMenu,
  OverflowMenuItem,
  Loading,
  InlineNotification,
  Pagination,
  Dropdown,
  MultiSelect,
} from '@carbon/react';
import { Book, Time, Trophy, PlayFilled, Star, StarFilled, Filter } from '@carbon/icons-react';

interface Course {
  id: string;
  title: string;
  description: string;
  category: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced';
  duration: string;
  modules: number;
  rating: number;
  students: number;
  instructor: string;
  price: number;
  thumbnail: string;
  topics: string[];
  certification: boolean;
}

const CoursesPage: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(9);

  useEffect(() => {
    loadCourses();
  }, []);

  useEffect(() => {
    filterCourses();
  }, [searchQuery, selectedCategory, selectedLevel, courses]);

  const loadCourses = async () => {
    try {
      setIsLoading(true);

      // Mock data - replace with actual API call
      const mockCourses: Course[] = [
        {
          id: 'course-001',
          title: 'Modern React Development',
          description: 'Master React with hooks, context, and modern patterns',
          category: 'Web Development',
          level: 'Intermediate',
          duration: '8 weeks',
          modules: 12,
          rating: 4.8,
          students: 1250,
          instructor: 'John Smith',
          price: 79.99,
          thumbnail: '/api/placeholder/300/200',
          topics: ['React', 'JavaScript', 'Web Development'],
          certification: true,
        },
        {
          id: 'course-002',
          title: 'Python for Data Science',
          description: 'Learn Python programming with focus on data analysis',
          category: 'Data Science',
          level: 'Beginner',
          duration: '10 weeks',
          modules: 15,
          rating: 4.7,
          students: 2100,
          instructor: 'Sarah Johnson',
          price: 89.99,
          thumbnail: '/api/placeholder/300/200',
          topics: ['Python', 'Data Science', 'Machine Learning'],
          certification: true,
        },
        {
          id: 'course-003',
          title: 'AWS Cloud Architecture',
          description: 'Design and deploy scalable cloud solutions on AWS',
          category: 'Cloud Computing',
          level: 'Advanced',
          duration: '12 weeks',
          modules: 18,
          rating: 4.9,
          students: 890,
          instructor: 'Mike Chen',
          price: 129.99,
          thumbnail: '/api/placeholder/300/200',
          topics: ['AWS', 'Cloud', 'DevOps'],
          certification: true,
        },
        {
          id: 'course-004',
          title: 'UI/UX Design Fundamentals',
          description: 'Create beautiful and functional user interfaces',
          category: 'Design',
          level: 'Beginner',
          duration: '6 weeks',
          modules: 10,
          rating: 4.6,
          students: 1560,
          instructor: 'Emily Davis',
          price: 69.99,
          thumbnail: '/api/placeholder/300/200',
          topics: ['Design', 'UI/UX', 'Figma'],
          certification: false,
        },
        {
          id: 'course-005',
          title: 'Node.js Backend Development',
          description: 'Build scalable backend services with Node.js',
          category: 'Web Development',
          level: 'Intermediate',
          duration: '8 weeks',
          modules: 14,
          rating: 4.7,
          students: 980,
          instructor: 'Alex Turner',
          price: 84.99,
          thumbnail: '/api/placeholder/300/200',
          topics: ['Node.js', 'JavaScript', 'Backend'],
          certification: true,
        },
        {
          id: 'course-006',
          title: 'Mobile App Development with Flutter',
          description: 'Create cross-platform mobile apps with Flutter',
          category: 'Mobile Development',
          level: 'Intermediate',
          duration: '10 weeks',
          modules: 16,
          rating: 4.8,
          students: 750,
          instructor: 'Lisa Wang',
          price: 94.99,
          thumbnail: '/api/placeholder/300/200',
          topics: ['Flutter', 'Mobile', 'Dart'],
          certification: true,
        },
      ];

      setCourses(mockCourses);
      setFilteredCourses(mockCourses);
    } catch (error) {
      console.error('Failed to load courses:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterCourses = () => {
    let filtered = courses;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(
        course =>
          course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          course.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          course.topics.some(topic => topic.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(course => course.category === selectedCategory);
    }

    // Level filter
    if (selectedLevel.length > 0) {
      filtered = filtered.filter(course => selectedLevel.includes(course.level));
    }

    setFilteredCourses(filtered);
    setCurrentPage(1);
  };

  const categories = ['all', ...Array.from(new Set(courses.map(c => c.category)))];
  const levels = ['Beginner', 'Intermediate', 'Advanced'];

  const paginatedCourses = filteredCourses.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<StarFilled key={i} size={16} />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<StarFilled key={i} size={16} style={{ opacity: 0.5 }} />);
      } else {
        stars.push(<Star key={i} size={16} />);
      }
    }
    return stars;
  };

  const CourseCard: React.FC<{ course: Course }> = ({ course }) => (
    <Tile className="course-card" style={{ padding: 0, overflow: 'hidden' }}>
      <div
        style={{
          height: '200px',
          background: 'var(--cds-ui-03)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Book size={48} />
      </div>
      <div style={{ padding: 'var(--cds-spacing-05)' }}>
        <h4 style={{ marginBottom: 'var(--cds-spacing-03)' }}>{course.title}</h4>
        <p
          style={{
            marginBottom: 'var(--cds-spacing-04)',
            color: 'var(--cds-text-secondary)',
            fontSize: '0.875rem',
          }}
        >
          {course.description}
        </p>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--cds-spacing-03)',
            marginBottom: 'var(--cds-spacing-03)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--cds-spacing-01)' }}>
            {renderStars(course.rating)}
            <span style={{ fontSize: '0.875rem' }}>({course.rating})</span>
          </div>
          <span style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)' }}>{course.students} students</span>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 'var(--cds-spacing-02)',
            marginBottom: 'var(--cds-spacing-04)',
            flexWrap: 'wrap',
          }}
        >
          <Tag type="blue" size="sm">
            {course.level}
          </Tag>
          <Tag type="gray" size="sm">
            <Time size={12} style={{ marginRight: '4px' }} />
            {course.duration}
          </Tag>
          {course.certification && (
            <Tag type="green" size="sm">
              <Trophy size={12} style={{ marginRight: '4px' }} />
              Certificate
            </Tag>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: '1.25rem', fontWeight: 600 }}>${course.price}</span>
          <Button kind="primary" size="sm" renderIcon={PlayFilled}>
            Enroll Now
          </Button>
        </div>
      </div>
    </Tile>
  );

  if (isLoading) {
    return (
      <Grid narrow>
        <Column lg={16}>
          <Loading description="Loading courses..." />
        </Column>
      </Grid>
    );
  }

  return (
    <Grid narrow>
      <Column lg={16}>
        <div style={{ marginBottom: 'var(--cds-spacing-06)' }}>
          <h1 style={{ marginBottom: 'var(--cds-spacing-05)' }}>Available Courses</h1>

          <Grid narrow>
            <Column lg={8} md={4} sm={4}>
              <Search
                placeholder="Search courses..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                labelText="Search"
              />
            </Column>
            <Column lg={4} md={2} sm={4}>
              <Dropdown
                id="category-filter"
                titleText="Category"
                label="All Categories"
                items={categories}
                selectedItem={selectedCategory}
                onChange={({ selectedItem }) => setSelectedCategory(selectedItem || 'all')}
              />
            </Column>
            <Column lg={4} md={2} sm={4}>
              <MultiSelect
                id="level-filter"
                titleText="Level"
                label="All Levels"
                items={levels.map(level => ({ id: level, text: level }))}
                selectedItems={selectedLevel.map(level => ({ id: level, text: level }))}
                onChange={({ selectedItems }) => setSelectedLevel(selectedItems.map((item: any) => item.id))}
              />
            </Column>
          </Grid>
        </div>

        {filteredCourses.length === 0 ? (
          <InlineNotification
            kind="info"
            title="No courses found"
            subtitle="Try adjusting your search filters"
            hideCloseButton
          />
        ) : (
          <>
            <Grid narrow>
              {paginatedCourses.map(course => (
                <Column key={course.id} lg={5} md={4} sm={4} style={{ marginBottom: 'var(--cds-spacing-05)' }}>
                  <CourseCard course={course} />
                </Column>
              ))}
            </Grid>

            {filteredCourses.length > pageSize && (
              <Pagination
                backwardText="Previous page"
                forwardText="Next page"
                itemsPerPageText="Items per page:"
                page={currentPage}
                pageNumberText="Page Number"
                pageSize={pageSize}
                pageSizes={[9, 18, 27]}
                totalItems={filteredCourses.length}
                onChange={({ page }) => setCurrentPage(page)}
              />
            )}
          </>
        )}
      </Column>
    </Grid>
  );
};

export default CoursesPage;
